/**
 * rd-r12 A2 — the one rule-amount codec per language.
 *
 * The frozen ``Rule`` schema carries no direction field
 * (``contracts/books-api.yaml``, ``additionalProperties: false``) and its
 * ``DecimalAmount`` pattern allows no leading ``+``, so direction rides on the
 * one sign a decimal string has (the Python reference is
 * ``pipeline.rules_engine.rule_amount_spec``):
 *
 * - unsigned (``"25.00"``) — the legacy meaning: this size, either direction.
 *   Every rule written before R2-07 is unsigned and keeps matching both ways.
 * - negative, non-zero (``"-25.00"``) — this size, money in only.
 * - negative zero (``"-0.00"``) — any size, money in only. This is what a
 *   deposit-minted vendor rule writes.
 *
 * Money out has no marker on purpose (a refund is a legitimate money-in
 * against an expense; money out against Income is always wrong).
 *
 * This module is a LEAF: no ``Env``, no D1, no ``./http``, no import from
 * ``@gv/contracts``. The portal imports it the way it imports
 * ``am_visible_text.ts``. It has no money formatter of its own — the Worker
 * has none and must not grow a second one — so ``ruleAmountWords`` takes the
 * caller's formatter (the portal passes ``moneyText``).
 *
 * Every decision about a rule's amount in the Worker and the portal goes
 * through here; the truth table both languages are checked against is
 * ``tests/fixtures/rule_amount_spec_cases.json``.
 *
 * Synthetic data only.
 */

export type RuleAmountDirection = "either" | "in";

export type RuleAmount = {
  /** Unsigned magnitude in canonical decimal form (``"25"`` for ``"25.00"``). */
  magnitude: string | null;
  /** ``"in"`` only when the spec binds money in; ``"either"`` otherwise. */
  direction: RuleAmountDirection;
};

/** The contract's ``DecimalAmount`` shape: ``^-?(0|[1-9]\d{0,12})(\.\d{1,2})?$``. */
const DECIMAL_AMOUNT = /^(?:0|[1-9]\d{0,12})(?:\.(\d{1,2}))?$/;

/**
 * Decode a stored ``match.amount`` into magnitude and direction.
 *
 * Throws for text that is not the contract's decimal shape: such a value
 * cannot be a rule pin, and reading it as "any amount" would widen a rule
 * silently. Callers that must not crash decide what an unreadable spec means
 * for them (the overlap classifier treats it as undecidable).
 */
export function decodeRuleAmount(spec: string | null | undefined): RuleAmount {
  if (spec === null || spec === undefined) {
    return { magnitude: null, direction: "either" };
  }
  const text = spec.trim();
  // An empty or whitespace-only pin is ABSENT, not unreadable: "any amount".
  // The portal used to print nothing at all for it, which made an empty pin
  // look like no pin on one face and a broken sentence on another.
  if (text === "") {
    return { magnitude: null, direction: "either" };
  }
  const negative = text.startsWith("-");
  const body = negative ? text.slice(1) : text;
  const parsed = DECIMAL_AMOUNT.exec(body);
  if (parsed === null) {
    throw new Error(`unreadable rule amount ${JSON.stringify(spec)}`);
  }
  const fraction = parsed[1] ?? "";
  const integer = body.slice(0, body.length - (fraction.length > 0 ? fraction.length + 1 : 0));
  const trimmedFraction = fraction.replace(/0+$/, "");
  const magnitude = trimmedFraction === "" ? integer : `${integer}.${trimmedFraction}`;
  if (!negative) return { magnitude, direction: "either" };
  // Negative zero is "any size, money in only" — zero has no magnitude pin.
  if (magnitude === "0") return { magnitude: null, direction: "in" };
  return { magnitude, direction: "in" };
}

/**
 * The same read for a caller that must not throw (the overlap classifier, the
 * faces): an unreadable spec is `null`, meaning "undecidable" — never "any
 * amount", which would widen a rule silently.
 */
export function readRuleAmount(
  spec: string | null | undefined,
): RuleAmount | null {
  try {
    return decodeRuleAmount(spec);
  } catch {
    return null;
  }
}

/**
 * Encode a magnitude and direction into a stored spec.
 *
 * ``encodeRuleAmount(null, "in")`` is ``"-0.00"`` (any size, money in only);
 * ``encodeRuleAmount(null, "either")`` is ``undefined`` (no amount pin). The
 * magnitude is written as the caller supplies it, so a rule minted from
 * ``"42.50"`` keeps both decimals.
 */
export function encodeRuleAmount(
  magnitude: string | null,
  direction: RuleAmountDirection,
): string | undefined {
  if (magnitude === null) {
    return direction === "in" ? "-0.00" : undefined;
  }
  return direction === "in" ? `-${magnitude}` : magnitude;
}

/**
 * The words for a person, built from the codec — never ``moneyText`` on a raw
 * spec (``moneyText("-25.00")`` reads as money out, the exact sign flip this
 * lane removes). The caller supplies the money formatter.
 */
export function ruleAmountWords(
  spec: string | null | undefined,
  money: (unsignedDecimal: string) => string,
): string {
  const decoded = readRuleAmount(spec);
  if (decoded === null) {
    // An unreadable pin is still a pin: the face says so plainly instead of
    // printing a size it cannot read or dropping the bit (a vanished pin looks
    // like "no pin", which is worse). The raw spec never reaches a face.
    return "an unreadable amount";
  }
  if (decoded.magnitude === null) {
    return decoded.direction === "in" ? "any amount, money in" : "any amount";
  }
  const text = money(decoded.magnitude);
  return decoded.direction === "in" ? `${text}, money in` : text;
}

/**
 * The noun a rule sentence uses for this spec (owner decision 8, resolution
 * E4): an unsigned rule reads "every payment or deposit"; a money-in rule
 * reads "every deposit". A money-out marker would read "every payment", but
 * the rule format has none today.
 */
export function ruleDirectionNoun(
  spec: string | null | undefined,
): "payment or deposit" | "deposit" {
  const decoded = readRuleAmount(spec);
  return decoded?.direction === "in" ? "deposit" : "payment or deposit";
}
